import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { AppDialog } from '../ui';
import { exportMockupsToCSV, ExportState, downloadPartialCSV } from '../../utils/csvExport';

export interface MockupsCsvExportProps {
    /** The current list query; the export walks every page of it. */
    queryParams: any;
    /** The list is loading: exporting is paused until it settles. */
    loading?: boolean;
}

/**
 * "Export CSV" for the preview grids. The export itself (day-by-day paging, resume, skip page / day,
 * partial download) lives in `utils/csvExport`; this is the button, its progress label and the
 * dialog shown when an export is interrupted.
 */
const MockupsCsvExport = ({ queryParams, loading = false }: MockupsCsvExportProps) => {
    const [exportLoading, setExportLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, currentDate: '', totalDates: 0 });
    const [exportError, setExportError] = useState<string | null>(null);
    const [exportState, setExportState] = useState<ExportState | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleExportCSV = async (resumeFromState?: ExportState, skipPage: boolean = false, skipDay: boolean = false) => {
        setExportLoading(true);
        setExportProgress({ current: 0, total: 0, currentDate: '', totalDates: 0 });
        setExportError(null);
        setDialogOpen(false);

        const estimatedRecords = queryParams?.total || 0;

        if (estimatedRecords > 50000 && !resumeFromState) {
            const sizeEstimateMB = Math.round((estimatedRecords * 500) / (1024 * 1024)); // ~500 bytes per record
            const message = `Warning: exporting ${estimatedRecords.toLocaleString()} records (~${sizeEstimateMB}MB) may take several minutes.\n\nAll data will be in one CSV file.`;
            const confirmed = window.confirm(message + '\n\nContinue?');
            if (!confirmed) {
                setExportLoading(false);
                return;
            }
        }

        try {
            await exportMockupsToCSV(
                queryParams,
                (current, total, currentDate, totalDates) => {
                    setExportProgress({ current, total, currentDate: currentDate || '', totalDates: totalDates || 0 });
                },
                resumeFromState,
                skipPage,
                skipDay
            );
            setExportState(null);
            if (estimatedRecords > 10000) {
                alert(`Export completed! Downloaded ${estimatedRecords.toLocaleString()} records in one CSV file.`);
            }
        } catch (error: any) {
            if (error.exportState) {
                setExportState(error.exportState);
                const { currentPage, totalPages, currentDate, currentDateIndex, datesList } = error.exportState;
                const dateInfo = currentDate && datesList ? ` Date: ${currentDate} (day ${(currentDateIndex || 0) + 1}/${datesList.length}).` : '';
                setExportError(`Export interrupted on page ${currentPage} of ${totalPages}.${dateInfo} Choose how to continue.`);
            } else {
                setExportError('Failed to export CSV. Please try again.');
            }
            setDialogOpen(true);
        } finally {
            setExportLoading(false);
        }
    };

    const handleRetryExport = () => {
        if (exportState) handleExportCSV(exportState, false, false);
    };

    const handleSkipPage = () => {
        if (!exportState) return;
        const confirmed = window.confirm(`Skip page ${exportState.currentPage + 1}?\n\nData from this page will NOT be included in the export.`);
        if (confirmed) handleExportCSV(exportState, true, false);
    };

    const handleSkipDay = () => {
        if (!exportState || !exportState.currentDate) return;
        const recordsInDay = exportState.recordsPerDay?.[exportState.currentDate] || 0;
        const confirmed = window.confirm(`Skip entire day ${exportState.currentDate}?\n\nAll data from this day will NOT be included in the export (~${recordsInDay.toLocaleString()} records).`);
        if (confirmed) handleExportCSV(exportState, false, true);
    };

    const handleSavePartial = () => {
        if (exportState && exportState.csvChunks && exportState.csvChunks.length > 0) {
            downloadPartialCSV(exportState.csvChunks, exportState.queryParams, exportState.currentPage, exportState.totalPages);
            alert(`Partial data saved!\n\nPages: 1-${exportState.currentPage} of ${exportState.totalPages}\nYou can try to continue the export or skip the problematic page.`);
        } else {
            alert('No data to save');
        }
    };

    /** Escape, backdrop or X only hide the dialog: the buffered pages stay until Cancel. */
    const hideDialog = () => setDialogOpen(false);

    const handleCancelExport = () => {
        setDialogOpen(false);
        setExportError(null);
        setExportState(null);
        setExportProgress({ current: 0, total: 0, currentDate: '', totalDates: 0 });
    };

    const label = exportLoading
        ? exportProgress.currentDate
            ? `${exportProgress.currentDate} (${exportProgress.current}/${exportProgress.total})`
            : `Exporting ${exportProgress.current}/${exportProgress.total}`
        : 'Export CSV';

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onClick={() => handleExportCSV()}
                disabled={exportLoading || loading}
                startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                aria-live="polite"
            >
                {label}
            </Button>
            {exportError && !dialogOpen && (
                <Button variant="outlined" size="small" color="warning" onClick={() => setDialogOpen(true)}>
                    {exportState ? 'Resume export' : 'Export failed'}
                </Button>
            )}
            <AppDialog
                open={dialogOpen && !!exportError}
                onClose={hideDialog}
                title="Export interrupted"
                description={exportError}
                actions={(
                    <>
                        <Button variant="text" onClick={handleCancelExport}>Cancel</Button>
                        <Button variant="outlined" startIcon={<SaveIcon />} onClick={handleSavePartial}>Save partial data</Button>
                        {exportState && (
                            <Button variant="outlined" startIcon={<SkipNextIcon />} onClick={handleSkipPage}>Skip page</Button>
                        )}
                        {exportState?.currentDate && (
                            <Button variant="outlined" startIcon={<SkipNextIcon />} onClick={handleSkipDay}>Skip day</Button>
                        )}
                        {exportState && (
                            <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRetryExport}>Try again</Button>
                        )}
                    </>
                )}
            >
                <p style={{ margin: 0, fontSize: 14 }}>
                    {exportState
                        ? 'Pages exported so far stay in memory until you cancel: save them as a partial file, retry the failed page, or skip it.'
                        : 'Nothing was exported. Check the connection and start the export again.'}
                </p>
            </AppDialog>
        </>
    );
};

export default MockupsCsvExport;
